// The step timeline (app.md §7), shown in the modal dialog behind the
// header's Pipeline button. Renders the workflow as clickable nodes in
// main-path order (Import Control → Manager Approval → Director Approval →
// Oversight Approval), with Controller Approval drawn DIRECTLY ABOVE Manager
// Approval as its alternative: Import Control splits into the two, and they
// join again into Director Approval — "-<>-", two parallel branches, since an
// invoice takes one or the other, never both (owner, 2026-10-09 — D-336
// amended: "We should always route to either Controller Approval or Manager
// Approval but never both. ... In the Pipeline, then, we should go back to
// having Controller Approval sit above Manager Approval as an alternative.").
// That restores D-303's drawing; for a few hours on 2026-10-09 D-336 drew one
// straight line instead. The two steps off the default route keep DOTTED
// rings, "to visually suggest that they are distinct from" the two on it, "in
// this case because they are off by default, whereas those are on by
// default." Since D-350 those are Manager Approval and Oversight Approval;
// until then, Controller Approval and Oversight Approval. Oversight Approval then runs straight on to Export Control and
// Closing Confirmation: the "--<" fork to Manual Entry that followed it went
// with that step (owner, 2026-10-07 — D-311). Denial Review hangs off the flow past a
// solid vertical divider with no connector at all (`renderTail`) — a deny can
// arrive from any approval step, so no single connector would be honest.
// Each node shows a waiting-count badge. Only nodes the user can act on are
// lit (in the accent color) — the timeline doubles as the role indicator.
// The selected node is filled accent. (A red stale-work dot on the node was
// tried and removed 2026-06-09 — not intuitive; aging lives in the queue
// rows.)
import { Fragment } from 'react'
import { STAFFED_NODE_KEYS as SHARED_STAFFED_NODE_KEYS } from '@ap/shared'
import { makeStyles, mergeClasses, shorthands, tokens, Caption1, Tooltip } from '@fluentui/react-components'
import { ArchiveRegular, CheckmarkRegular, WarningFilled } from '@fluentui/react-icons'
import type { ApprovalRequirement } from '@ap/shared'
import { TIMELINE_NODES, type NodeKey, type Role, type TimelineNode } from '../lib/constants'
import type { StuckNode } from '../lib/data'
import {
  NODE_W,
  NODE_H,
  NODE_GAP,
  NODE_PAD_Y,
  CIRCLE_C,
  CIRCLE_D,
  BRANCH_GAP,
  FORK_W,
  FORK_H,
  CONNECTOR_MIN_W,
  DIVIDER_MARGIN,
  BADGE_D,
  BADGE_TOP,
  BADGE_RIGHT,
  alternativeAbove,
} from '../lib/timelineLayout'

// The fork glyph and row centering depend on these fixed sizes (now imported
// from `../lib/timelineLayout`, which also derives the dialog's min width
// from them): a node is NODE_H tall with its circle's center CIRCLE_C from
// the top; the two branch rows stack with BRANCH_GAP between them.

const useStyles = makeStyles({
  root: { display: 'flex', flexDirection: 'column', gap: '4px' },
  chainCentered: { display: 'flex', alignItems: 'center' },
  // Controller Approval stacked directly above Manager Approval (D-303): two
  // rows, BRANCH_GAP apart, only as wide as one node.
  alternatives: { display: 'flex', flexDirection: 'column', gap: `${BRANCH_GAP}px`, flexShrink: 0 },
  node: {
    display: 'flex',
    flexDirection: 'column',
    // NODE_H now leaves ~21px of slack under a one-line label (30px label box
    // vs. a 13.2px line), so the flex line must start at the top rather than
    // rely on the UA button default — CIRCLE_C = 24 (which ForkGlyph and the
    // connector `marginTop` are computed from) is only true while it does.
    justifyContent: 'flex-start',
    alignItems: 'center',
    gap: `${NODE_GAP}px`,
    width: `${NODE_W}px`,
    height: `${NODE_H}px`,
    background: 'none',
    border: 'none',
    padding: `${NODE_PAD_Y}px 0`,
    fontFamily: 'inherit',
  },
  circle: {
    position: 'relative',
    width: `${CIRCLE_D}px`,
    height: `${CIRCLE_D}px`,
    // Keep it a circle: the node is a fixed-height flex column, so a two-line
    // label (e.g. "Primary Approval") would otherwise let flex compress the
    // circle vertically into an oval. flexShrink:0 pins it at 40×40; a long
    // label uses the space below instead.
    flexShrink: 0,
    boxSizing: 'border-box',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: `2px solid ${tokens.colorNeutralStroke2}`,
    color: tokens.colorNeutralForeground3,
    backgroundColor: tokens.colorNeutralBackground1,
  },
  lit: {
    border: `2px solid ${tokens.colorBrandStroke1}`,
    color: tokens.colorBrandForeground1,
    cursor: 'pointer',
  },
  selected: {
    backgroundColor: tokens.colorBrandBackground,
    border: `2px solid ${tokens.colorBrandBackground}`,
    color: tokens.colorNeutralForegroundOnBrand,
  },
  muted: { opacity: 0.5, cursor: 'default' },
  // The Add/Edit Rule picker's step: clickable on or off (D-342).
  pickable: { cursor: 'pointer' },
  pickGlyph: { fontSize: '20px', display: 'flex' },
  // OFF BY DEFAULT (D-336): Controller Approval and Oversight Approval. Only
  // the style changes; the ring keeps its width and colour in every state, lit,
  // muted or selected.
  dotted: { ...shorthands.borderStyle('dotted') },
  label: { textAlign: 'center', lineHeight: 1.1 },
  labelMuted: { color: tokens.colorNeutralForeground3 },
  // Waiting count rendered inside the node circle.
  count: { fontSize: tokens.fontSizeBase400, fontWeight: tokens.fontWeightSemibold, lineHeight: 1 },
  dot: { fontSize: tokens.fontSizeBase300, lineHeight: 1 },
  // Archives node shows a glyph, not a count — its queue is unbounded.
  glyph: { fontSize: '20px', display: 'flex' },
  connector: {
    height: '2px',
    flex: 1,
    minWidth: `${CONNECTOR_MIN_W}px`,
    backgroundColor: tokens.colorNeutralStroke2,
    marginTop: `${CIRCLE_C - 1}px`,
  },
  forkGlyph: { flexShrink: 0, color: tokens.colorNeutralStroke2 },
  // Separates the branch destinations (Denial Review) from the flow proper —
  // a deny can arrive from any approval step, so no single connector is
  // honest. Vertically spans most of the fork block (v1.31 user note).
  vDivider: {
    width: '2px',
    height: `${FORK_H - 24}px`,
    backgroundColor: tokens.colorNeutralStroke2,
    borderRadius: '1px',
    flexShrink: 0,
    margin: `0 ${DIVIDER_MARGIN}px`,
  },
  // The warning icon's positioning context. A SIBLING of the button (never a
  // child): the button carries `disabled={!lit}`, and a disabled button fires
  // no mouse events in Chrome, so a tooltip trigger inside it would never
  // open — precisely the unlit case that matters, since an internal caller
  // (who chases a frozen company) has no tier node lit. Otherwise unstyled:
  // it becomes the flex item in both `s.chain` and `s.chainCentered` and is
  // sized by the 112x84 button inside it, so the connector marginTop offsets
  // above are unaffected.
  nodeWrapper: { position: 'relative' },
  stuckTrigger: {
    position: 'absolute',
    // STRADDLES THE CIRCLE'S TOP-RIGHT EDGE, so it reads as belonging to this
    // step. `BADGE_TOP`/`BADGE_RIGHT` (`../lib/timelineLayout`) are DERIVED,
    // not guessed: the circle centres in the node at (NODE_W / 2, CIRCLE_C),
    // and the badge sits at a fixed (BADGE_DX, BADGE_DY) = (16, 12) offset
    // from that centre — a distance the test file pins at exactly CIRCLE_D / 2
    // (20), i.e. on the stroke. At today's NODE_W 112 / CIRCLE_D 40 / CIRCLE_C
    // 24 that resolves to top 3px, right 31px: the circle spans x 36-76, y
    // 4-44 (centre 56,24, radius 20), the BADGE_D 18px badge spans x 63-81, y
    // 3-21 (centre 72,12). `right: 14px` (the first attempt) put it at x
    // 88-106: entirely OFF the circle, floating in the node's own whitespace.
    // NODE_H never enters this arithmetic — only the node's WIDTH does. Because
    // these are now derived from NODE_W/CIRCLE_D/CIRCLE_C rather than
    // hardcoded, a change to any of those moves the badge instead of quietly
    // sliding it off the stroke.
    top: `${BADGE_TOP}px`,
    right: `${BADGE_RIGHT}px`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: `${BADGE_D}px`,
    height: `${BADGE_D}px`,
    borderRadius: '50%',
    // An opaque disc behind the glyph. It has to be there: the badge sits ON
    // the circle's stroke, and a selected node's circle is filled with the
    // brand color, which the warning foreground does not read against.
    backgroundColor: tokens.colorNeutralBackground1,
    color: tokens.colorStatusWarningForeground1,
    cursor: 'default',
  },
  stuckIcon: { fontSize: '16px' },
})

/** Cap the named companies so the tooltip cannot grow to the full 38-company list. */
const MAX_NAMED_COMPANIES = 4

/**
 * Only the company-scoped nodes can freeze, so only they may carry the icon —
 * whatever the payload happens to contain. Archives and the internal steps have
 * no per-company staffing to remove, and the sentence ("nobody is assigned to
 * this step for …") would be nonsense on them. `ap-ar` sat here too from
 * issue 3 (2026-08-11) until AP/AR Approval merged into Import Control
 * (owner, 2026-09-29 — D-215, D-221). `STAFFED_NODE_KEYS` from `@ap/shared`.
 */
const STAFFED_NODE_KEYS: ReadonlySet<NodeKey> = new Set<NodeKey>(SHARED_STAFFED_NODE_KEYS)

/**
 * Module-private (not exported): a second export from a component file fails
 * `react-refresh/only-export-components`.
 */
function stuckTooltipText(tier: StuckNode, nodeKey: NodeKey): string {
  const invoiceWord = tier.count === 1 ? 'invoice' : 'invoices'
  // THE OVERSIGHT SEAT IS ALL-COMPANIES (owner, 2026-09-15), so naming the
  // companies with nobody assigned is meaningless here: the fact is not that
  // some companies are unstaffed, it is that nobody holds Oversight at all.
  // Listing companies would also invite somebody to fix it per company, which
  // is not a thing that can be done. The other staffed nodes ARE company-scoped
  // and keep the sentence below.
  if (nodeKey === 'oversight') {
    return `${tier.count} ${invoiceWord} cannot move: nobody is assigned to Oversight Approval.`
  }
  const named = tier.companies.slice(0, MAX_NAMED_COMPANIES)
  const remainder = tier.companies.length - named.length
  // With one company its count IS the tier count, so the parenthesis would
  // print the same number twice — "3 invoices … for SeniorLIFE Altoona (3)".
  const namedText =
    tier.companies.length === 1
      ? tier.companies[0].company_name
      : named.map((c) => `${c.company_name} (${c.count})`).join(', ')
  const suffix = remainder > 0 ? `, and ${remainder} more` : ''
  return `${tier.count} ${invoiceWord} cannot move: nobody is assigned to this step for ${namedText}${suffix}.`
}

interface NodeProps {
  label: string
  count: number
  lit: boolean
  selected: boolean
  onSelect: () => void
  /** Archive node: show a glyph instead of a waiting count. */
  archive?: boolean
  /** Present only for one of the `STAFFED_NODE_KEYS` nodes with something stuck. */
  stuck?: StuckNode
  /** Which node this is — the freeze wording differs for Oversight, whose seat
      is All-companies and so has no per-company story to tell. */
  nodeKey: NodeKey
  /** Off by default, so its ring is dotted (D-336). */
  optional?: boolean
}

function Node({ label, count, lit, selected, onSelect, archive, stuck, nodeKey, optional }: NodeProps) {
  const s = useStyles()
  const circleClass = mergeClasses(s.circle, lit ? s.lit : s.muted, selected && s.selected, optional && s.dotted)
  return (
    <div className={s.nodeWrapper}>
      <button
        type="button"
        className={s.node}
        onClick={lit ? onSelect : undefined}
        disabled={!lit}
        aria-pressed={selected}
      >
        <div className={circleClass}>
          {archive ? (
            <ArchiveRegular className={s.glyph} />
          ) : lit ? (
            <span className={s.count}>{count}</span>
          ) : (
            <span className={s.dot}>●</span>
          )}
        </div>
        <Caption1 className={mergeClasses(s.label, !lit && s.labelMuted)}>{label}</Caption1>
      </button>
      {stuck !== undefined && (
        <Tooltip
          content={stuckTooltipText(stuck, nodeKey)}
          relationship="label"
          withArrow
          positioning="above"
        >
          {/* `role="img"` is required, not decoration: `relationship="label"`
              puts an `aria-label` on this element, and `aria-label` is
              PROHIBITED on the implicit `generic` role a bare <span> carries —
              axe reports `aria-prohibited-attr` and a screen reader may drop
              the sentence entirely. `img` is the right role for an icon that
              conveys information and takes no action (hence `cursor: default`
              and no handler); `tabIndex={0}` is what makes the tooltip
              keyboard-reachable, which `docs/app.md` promises. */}
          <span role="img" tabIndex={0} className={s.stuckTrigger}>
            <WarningFilled className={s.stuckIcon} />
          </span>
        </Tooltip>
      )}
    </div>
  )
}

// "--<": a short line in from the node before the split, a vertical spine
// between the two branch rows' circle centers, and a stub into each of
// Controller Approval and Manager Approval (two equal routes, D-303).
//
// `join` mirrors it, ">--": the two branches' stubs meet on the spine and one
// line leaves it at the centre, into the node the branches feed.
function ForkGlyph({ join = false }: { join?: boolean }) {
  const s = useStyles()
  const mid = FORK_H / 2 - NODE_H / 2 + CIRCLE_C // anchor circle's center
  const top = CIRCLE_C
  const bottom = NODE_H + BRANCH_GAP + CIRCLE_C
  const x = FORK_W / 2
  // The side the single line is on, and the side the two stubs are on.
  const [single, pair] = join ? [FORK_W, 0] : [0, FORK_W]
  return (
    <svg
      className={s.forkGlyph}
      width={FORK_W}
      height={FORK_H}
      viewBox={`0 0 ${FORK_W} ${FORK_H}`}
      aria-hidden="true"
    >
      <path d={`M${single} ${mid} H${x}`} stroke="currentColor" strokeWidth="2" fill="none" />
      <path d={`M${x} ${top} V${bottom}`} stroke="currentColor" strokeWidth="2" fill="none" />
      <path d={`M${x} ${top} H${pair}`} stroke="currentColor" strokeWidth="2" fill="none" />
      <path d={`M${x} ${bottom} H${pair}`} stroke="currentColor" strokeWidth="2" fill="none" />
    </svg>
  )
}

interface Props {
  litRoles: Set<Role>
  selected: NodeKey | 'admin' | 'dashboard' | null
  counts: Record<string, number>
  onSelect: (key: NodeKey) => void
  /** The `STAFFED_NODE_KEYS` nodes with something stuck, keyed by node key. A
      node absent here draws no warning icon — including the common case,
      nothing stuck at all. */
  stuck?: Partial<Record<string, StuckNode>>
}

export function Timeline({ litRoles, selected, counts, onSelect, stuck }: Props) {
  const s = useStyles()
  // Main path first, then any branch node (Denial Review), then the archive
  // node (rendered separately, past its own divider).
  const main = TIMELINE_NODES.filter((n) => !n.branch && !n.archive && !n.alternativeTo)
  const branch = TIMELINE_NODES.filter((n) => n.branch)
  const archive = TIMELINE_NODES.find((n) => n.archive)

  const renderNode = (n: TimelineNode) => (
    <Node
      label={n.label}
      count={counts[n.key] ?? 0}
      lit={litRoles.has(n.role)}
      selected={selected === n.key}
      onSelect={() => onSelect(n.key)}
      archive={n.archive}
      stuck={STAFFED_NODE_KEYS.has(n.key) ? stuck?.[n.key] : undefined}
      nodeKey={n.key}
      optional={n.optional}
    />
  )

  // Denial Review + Archives both hang off the flow proper, each past a
  // vertical divider — a deny arrives from any approval step, and Archives is a
  // terminal lookup, not a sequential step.
  const renderTail = () => (
    <>
      {branch.length > 0 && <div className={s.vDivider} aria-hidden />}
      {branch.map((n) => (
        <Fragment key={n.key}>{renderNode(n)}</Fragment>
      ))}
      {archive && <div className={s.vDivider} aria-hidden />}
      {archive && renderNode(archive)}
    </>
  )

  // The centred segment: a FORK_H-tall row whose plain nodes sit in its middle
  // and whose connectors are pinned to their circle centres. A node with an
  // alternative (Manager Approval, under Controller Approval — D-303) becomes
  // a split glyph, the two nodes stacked with the alternative on top, and a
  // join glyph; the glyphs stand in for the connectors either side of it.
  const renderSegment = (nodes: TimelineNode[]) =>
    nodes.map((n, i) => {
      const alternative = alternativeAbove(TIMELINE_NODES, n)
      const next = nodes[i + 1]
      const connectorAfter =
        next !== undefined && alternative === undefined && alternativeAbove(TIMELINE_NODES, next) === undefined
      return (
        <Fragment key={n.key}>
          {alternative ? (
            <>
              <ForkGlyph />
              <div className={s.alternatives}>
                {renderNode(alternative)}
                {renderNode(n)}
              </div>
              <ForkGlyph join />
            </>
          ) : (
            renderNode(n)
          )}
          {connectorAfter && (
            // The centered row is FORK_H tall; pin the connector to the
            // left nodes' circle-center height rather than the row middle.
            <div
              className={s.connector}
              style={{ alignSelf: 'flex-start', marginTop: (FORK_H - NODE_H) / 2 + CIRCLE_C - 1 }}
            />
          )}
        </Fragment>
      )
    })

  return (
    <div className={s.root}>
      <div className={s.chainCentered}>
        {renderSegment(main)}
        {renderTail()}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// The Add/Edit Rule step picker (owner, 2026-10-09 — D-342)
// ---------------------------------------------------------------------------
//
// "In the Add/Edit Rule pop-up, I wonder if we can replace the basic toggles
// with a visual, like Pipeline has already. The user should still be toggling
// steps on and off in the same way ... We do not need to include Import
// Control, Export Control, or anything thereafter." And, of a first drawing
// made apart from this file: "the visual that you drew does not look quite like
// the Pipeline". So it is drawn HERE, from the Pipeline's own node, fork glyph,
// connector and sizes: Controller Approval above Manager Approval as its
// alternative, joining into Director Approval, then Oversight Approval, with the
// same dotted rings on the two steps that are off by default.
//
// A REQUIRED STEP IS FILLED with a checkmark — the Pipeline's selected node; a
// skipped one is the plain ring. Each node is a switch, so it still toggles on
// and off exactly as the four switches did.

/** The Pipeline node each requirement draws as. */
const REQUIREMENT_NODE: Record<ApprovalRequirement, NodeKey> = {
  controller: 'controller',
  manager: 'department',
  director: 'director',
  oversight: 'oversight',
}

function PickNode({
  requirement,
  on,
  onToggle,
}: {
  requirement: ApprovalRequirement
  on: boolean
  onToggle: (requirement: ApprovalRequirement, on: boolean) => void
}) {
  const s = useStyles()
  const node = TIMELINE_NODES.find((n) => n.key === REQUIREMENT_NODE[requirement])
  const label = node?.label ?? requirement
  return (
    <div className={s.nodeWrapper}>
      <button
        type="button"
        role="switch"
        aria-checked={on}
        aria-label={label}
        className={s.node}
        onClick={() => onToggle(requirement, !on)}
      >
        <div className={mergeClasses(s.circle, s.lit, s.pickable, on && s.selected, node?.optional && s.dotted)}>
          {on && <CheckmarkRegular className={s.pickGlyph} />}
        </div>
        <Caption1 className={mergeClasses(s.label, !on && s.labelMuted)}>{label}</Caption1>
      </button>
    </div>
  )
}

export function ApprovalPathPicker({
  required,
  onToggle,
}: {
  required: Readonly<Record<ApprovalRequirement, boolean>>
  onToggle: (requirement: ApprovalRequirement, on: boolean) => void
}) {
  const s = useStyles()
  // The plain nodes after the join sit in the middle of the FORK_H-tall row; a
  // connector between them is pinned to their circle centres, as in the
  // Pipeline's own `renderSegment`.
  const connector = (
    <div
      className={s.connector}
      style={{ alignSelf: 'flex-start', marginTop: (FORK_H - NODE_H) / 2 + CIRCLE_C - 1 }}
    />
  )
  return (
    <div className={s.root} role="group" aria-label="Required approvals">
      <div className={s.chainCentered}>
        <ForkGlyph />
        <div className={s.alternatives}>
          <PickNode requirement="controller" on={required.controller} onToggle={onToggle} />
          <PickNode requirement="manager" on={required.manager} onToggle={onToggle} />
        </div>
        <ForkGlyph join />
        {connector}
        <PickNode requirement="director" on={required.director} onToggle={onToggle} />
        {connector}
        <PickNode requirement="oversight" on={required.oversight} onToggle={onToggle} />
      </div>
    </div>
  )
}
